import { useCallback, useEffect, useState } from "react";
import { Gauge, RefreshCw, Timer, AlertTriangle, Users, Hourglass, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage, formatTanggalID } from "@/lib/api";

const TONE = {
  Habis: "bg-rose-50 text-rose-700 border-rose-200",
  "Hampir Habis": "bg-amber-50 text-amber-700 border-amber-200",
  Aman: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function KuotaLemburUnit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [terakhir, setTerakhir] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/overtime/quota/unit");
      setData(data);
      setTerakhir(new Date());
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 60000);
    return () => clearInterval(iv);
  }, [load]);

  const t = data?.totals;
  const p = data?.periode;

  return (
    <div data-testid="kuota-lembur-unit-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Monitoring Atasan Unit
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Kuota Lembur Tim
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Sisa kuota jam lembur seluruh staf di{" "}
            <b>{data?.unit_nama || "unit kerja Anda"}</b> untuk periode berjalan.
            {p ? (
              <>
                {" "}Periode <b>{formatTanggalID(p.start)} – {formatTanggalID(p.end)}</b> ({p.timezone}).
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1.5">
          <Button
            variant="outline"
            onClick={() => load()}
            disabled={loading}
            className="gap-2"
            data-testid="kuota-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Muat Ulang
          </Button>
          <span className="text-[10px] text-slate-400">
            {terakhir ? `Diperbarui ${terakhir.toLocaleTimeString("id-ID")} · otomatis tiap 60 detik` : "Memuat…"}
          </span>
        </div>
      </div>

      {data?.dikunci_ke_unit ? (
        <div
          className="mt-4 flex items-start gap-2 rounded-lg border border-teal-100 bg-teal-50/70 px-4 py-3 text-xs text-teal-900"
          data-testid="kuota-scope-notice"
        >
          <ShieldCheck size={14} className="mt-0.5 shrink-0" />
          <span>
            Data terkunci pada Unit Kerja Anda — pegawai dari unit lain tidak ditampilkan.
          </span>
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Staf Dipantau" value={t?.jumlah_staf ?? 0} icon={Users} />
        <Stat label="Kuota / Orang" value={`${data?.kuota_bulanan_jam ?? 0} jam`} icon={Gauge} />
        <Stat label="Total Terpakai" value={`${t?.total_terpakai_jam ?? 0} jam`} icon={Timer} tone="sky" />
        <Stat label="Total Sisa" value={`${t?.total_sisa_jam ?? 0} jam`} icon={Hourglass} tone="emerald" />
        <Stat
          label="Perlu Perhatian"
          value={`${(t?.staf_habis ?? 0) + (t?.staf_hampir_habis ?? 0)} staf`}
          icon={AlertTriangle}
          tone="rose"
        />
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-600">
            Rincian per Staf
          </div>
          <div className="text-[11px] text-slate-500">
            {t?.total_pending ?? 0} pengajuan menunggu · {t?.total_berjalan ?? 0} disetujui belum diaktualkan
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Staf", "Jabatan", "Kuota", "Terpakai", "Sisa", "Pemakaian", "Menunggu", "Status"].map((h) => (
                  <TableHead key={h} className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && !data ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-slate-400 py-10">
                    Memuat data…
                  </TableCell>
                </TableRow>
              ) : (data?.rows || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-slate-400 py-10">
                    Belum ada staf aktif pada unit kerja ini.
                  </TableCell>
                </TableRow>
              ) : (
                data.rows.map((r) => (
                  <TableRow key={r.user_id} data-testid={`kuota-row-${r.user_id}`}>
                    <TableCell>
                      <div className="font-semibold text-slate-900">{r.nama}</div>
                      <div className="text-xs text-slate-500 font-mono-clock">{r.npp}</div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{r.jabatan_nama}</TableCell>
                    <TableCell className="font-mono-clock text-sm text-slate-700">{r.kuota_jam} j</TableCell>
                    <TableCell className="font-mono-clock text-sm text-sky-800 font-semibold">
                      {r.terpakai_jam} j
                    </TableCell>
                    <TableCell
                      className="font-mono-clock text-sm font-extrabold text-teal-800"
                      data-testid={`kuota-sisa-${r.user_id}`}
                    >
                      {r.sisa_jam} j
                    </TableCell>
                    <TableCell className="min-w-[140px]">
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            r.status_kuota === "Habis"
                              ? "bg-rose-500"
                              : r.status_kuota === "Hampir Habis"
                                ? "bg-amber-500"
                                : "bg-emerald-500"
                          }`}
                          style={{ width: `${Math.min(r.persen_terpakai, 100)}%` }}
                        />
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">{r.persen_terpakai}% terpakai</div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {r.pending_count ? `${r.pending_count} (${r.pending_jam} j)` : "—"}
                      {r.berjalan_count ? (
                        <div className="text-[10px] text-amber-700">
                          {r.berjalan_count} belum aktual
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${TONE[r.status_kuota]}`}
                        data-testid={`kuota-status-${r.user_id}`}
                      >
                        {r.status_kuota}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = "slate" }) {
  const tones = {
    slate: "text-slate-700 bg-slate-50 border-slate-200",
    sky: "text-sky-700 bg-sky-50 border-sky-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold flex items-center gap-1.5 opacity-80">
        <Icon size={12} /> {label}
      </div>
      <div className="text-xl font-extrabold mt-1.5 leading-none">{value}</div>
    </div>
  );
}
